import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
    Pause,
    Check,
    Trash2,
    Tag,
    MessageSquare,
    Send,
    Clock,
} from "lucide-react";
import type { Task, TaskComment } from "@/types";
import { formatTime, getElapsedSeconds } from "@/utils/helpers";
import { TaskComments } from "@/components/TaskComments";
import { TagDisplay, TagInput } from "@/components/TagInput";

interface ActiveTaskCardProps {
    task: Task;
    onPause: () => void;
    onComplete: () => void;
    onDelete: () => void;
    onAddTime: (minutes: number) => void;
    onEditComment: (commentId: string, newText: string) => void;
    onDeleteComment: (commentId: string) => void;
    onUpdateTags: (tags: string[]) => void;
    onAddComment: (text: string) => void;
    reflectionCountdown?: number; // seconds until next reflection
    reflectionEnabled?: boolean;
    allTags?: string[]; // All existing tags for autocomplete
}

export function ActiveTaskCard({
    task,
    onPause,
    onComplete,
    onDelete,
    onAddTime,
    onEditComment,
    onDeleteComment,
    onUpdateTags,
    onAddComment,
    reflectionCountdown = 0,
    reflectionEnabled = false,
    allTags = [],
}: ActiveTaskCardProps) {
    const [showTagEdit, setShowTagEdit] = useState(false);
    const [showAddComment, setShowAddComment] = useState(false);
    const [newComment, setNewComment] = useState("");
    const totalElapsed = getElapsedSeconds(task);

    const handleAddComment = () => {
        if (newComment.trim()) {
            onAddComment(newComment.trim());
            setNewComment("");
            setShowAddComment(false);
        }
    };

    const formatCountdown = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };
    const remainingSeconds = task.estimatedMinutes * 60 - totalElapsed;
    const isOvertime = remainingSeconds < 0;
    const progress = Math.min(
        100,
        (totalElapsed / (task.estimatedMinutes * 60)) * 100
    );

    return (
        <Card
            className={`overflow-hidden border-0 shadow-xl ${
                isOvertime ? "ring-2 ring-red-500" : "ring-2 ring-green-500"
            }`}
        >
            <div
                className={`${
                    isOvertime
                        ? "bg-gradient-to-br from-red-500 to-orange-600"
                        : "bg-gradient-to-br from-green-500 to-emerald-600"
                } p-1`}
            >
                <div className="bg-card rounded-t-lg">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <div
                                    className={`w-2.5 h-2.5 ${
                                        isOvertime
                                            ? "bg-red-500"
                                            : "bg-green-500"
                                    } rounded-full animate-pulse`}
                                />
                                <span
                                    className={
                                        isOvertime
                                            ? "text-red-600"
                                            : "text-green-600"
                                    }
                                >
                                    {isOvertime ? "Overtime!" : "In Progress"}
                                </span>
                            </CardTitle>
                            <Badge
                                variant={isOvertime ? "destructive" : "success"}
                            >
                                {isOvertime ? "Over Time" : "Focus Mode"}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="pb-6">
                        <div className="text-xl font-semibold mb-2">
                            {task.name}
                        </div>

                        {/* Tags */}
                        <div className="mb-4">
                            {showTagEdit ? (
                                <div className="space-y-2">
                                    <TagInput
                                        tags={task.tags || []}
                                        onChange={(tags) => {
                                            onUpdateTags(tags);
                                        }}
                                        placeholder="Add tags..."
                                        allTags={allTags}
                                    />
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 text-xs"
                                        onClick={() => setShowTagEdit(false)}
                                    >
                                        Done
                                    </Button>
                                </div>
                            ) : task.tags && task.tags.length > 0 ? (
                                <div
                                    className="cursor-pointer"
                                    onClick={() => setShowTagEdit(true)}
                                    title="Click to edit tags"
                                >
                                    <TagDisplay tags={task.tags} size="sm" />
                                </div>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-xs text-muted-foreground"
                                    onClick={() => setShowTagEdit(true)}
                                >
                                    <Tag className="w-3 h-3 mr-1" />
                                    Add tags
                                </Button>
                            )}
                        </div>

                        <div className="flex items-center justify-center gap-4 sm:gap-8 mb-6">
                            <div className="text-center">
                                <div
                                    className={`text-3xl sm:text-5xl font-mono font-bold tracking-tight ${
                                        isOvertime
                                            ? "text-red-500"
                                            : "text-foreground"
                                    }`}
                                >
                                    {formatTime(remainingSeconds)}
                                </div>
                                <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                                    {isOvertime ? "overtime" : "remaining"}
                                </div>
                            </div>
                            <Separator
                                orientation="vertical"
                                className="h-12 sm:h-16"
                            />
                            <div className="text-center">
                                <div className="text-xl sm:text-3xl font-mono text-muted-foreground">
                                    {formatTime(totalElapsed)}
                                </div>
                                <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                                    elapsed
                                </div>
                            </div>
                        </div>

                        <Progress
                            value={progress}
                            className="h-3 mb-6"
                            indicatorClassName={
                                isOvertime ? "bg-red-500" : "bg-green-500"
                            }
                        />

                        <div className="flex items-center justify-center gap-2 flex-wrap">
                            <Button
                                variant="outline"
                                onClick={onPause}
                                className="gap-1"
                            >
                                <Pause className="w-4 h-4" />
                                Pause
                            </Button>
                            <div className="flex gap-1">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => onAddTime(5)}
                                >
                                    +5m
                                </Button>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => onAddTime(10)}
                                >
                                    +10m
                                </Button>
                            </div>
                            <Button
                                onClick={onComplete}
                                className="bg-green-600 hover:bg-green-700 gap-1"
                            >
                                <Check className="w-4 h-4" />
                                Complete
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={onDelete}
                                className="text-muted-foreground hover:text-destructive"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>

                        {/* Reflection Countdown */}
                        {reflectionEnabled && reflectionCountdown > 0 && (
                            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/30 rounded-lg py-2 px-3">
                                <Clock className="w-4 h-4 text-blue-500" />
                                <span>
                                    Next reflection in{" "}
                                    <span className="font-mono font-medium text-blue-600 dark:text-blue-400">
                                        {formatCountdown(reflectionCountdown)}
                                    </span>
                                </span>
                            </div>
                        )}

                        {/* Comments Section */}
                        <div className="mt-6 pt-4 border-t">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                                    <MessageSquare className="w-4 h-4" />
                                    Progress Notes
                                </span>
                                {!showAddComment && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 text-xs"
                                        onClick={() => setShowAddComment(true)}
                                    >
                                        + Add note
                                    </Button>
                                )}
                            </div>

                            {showAddComment && (
                                <div className="flex gap-2 mb-3">
                                    <Input
                                        placeholder="What have you accomplished?"
                                        value={newComment}
                                        onChange={(e) =>
                                            setNewComment(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter")
                                                handleAddComment();
                                            if (e.key === "Escape") {
                                                setShowAddComment(false);
                                                setNewComment("");
                                            }
                                        }}
                                        className="h-9"
                                        autoFocus
                                    />
                                    <Button
                                        size="icon"
                                        onClick={handleAddComment}
                                        disabled={!newComment.trim()}
                                        className="h-9 w-9"
                                    >
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}

                            {task.comments && task.comments.length > 0 ? (
                                <TaskComments
                                    comments={task.comments}
                                    onEdit={(commentId, text) =>
                                        onEditComment(commentId, text)
                                    }
                                    onDelete={onDeleteComment}
                                    maxVisible={3}
                                />
                            ) : (
                                <p className="text-xs text-muted-foreground text-center py-2">
                                    No notes yet. Add one to track your
                                    progress!
                                </p>
                            )}
                        </div>
                    </CardContent>
                </div>
            </div>
        </Card>
    );
}
